/**
 * Closing a message that has something written in it: keep it in Drafts,
 * discard it, or go back to it. Asked rather than decided, so a draft is
 * neither kept nor lost without the person knowing (Edmund, 2026-11-02).
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';

export function CloseDraft({
  existing,
  onSave,
  onDiscard,
  onBack,
}: {
  /** It was opened from Drafts: discarding deletes it from there. */
  existing: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onBack: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onBack();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onBack]);

  return createPortal(
    <div className="modal-scrim" role="presentation">
      <div
        className="close-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="close-dialog-title"
        aria-describedby="close-dialog-lead"
        // Keys pressed here stay here: the composer's Cmd+Enter must not send.
        onKeyDown={(e) => e.stopPropagation()}
      >
        <h3 id="close-dialog-title">Save this message to Drafts?</h3>
        <p id="close-dialog-lead">
          {existing
            ? 'Saved, it stays in Drafts with your changes. Discarded, it is deleted from Drafts.'
            : 'Saved, you can finish it later from Drafts. Discarded, it is deleted.'}
        </p>
        <div className="close-dialog-acts">
          <button type="button" className="btn ghost" onClick={onBack}>
            Keep writing
          </button>
          <button type="button" className="btn ghost" onClick={onDiscard}>
            Discard
          </button>
          <button type="button" className="btn" onClick={onSave} autoFocus>
            Save to Drafts
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
